import clsx from 'clsx';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { useCurrentUser, useView } from '@/hooks';
import { Typography } from '@/ui';

import type { FC, ReactNode } from 'react';

import CoachCalInfo from '@/components/Cal/CoachInfo';
import LockedFeature from '@/components/LockedStates';
import { useFeatureLocked } from '@/hooks/useFeatureLocked';
import { isCoach } from '@/types/user';
import PageHeaderLayout from '@/ui/components/PageHeaderLayout';

interface SessionsLayoutProps {
  children: ReactNode;
}

const SessionsLayout: FC<SessionsLayoutProps> = ({ children }) => {
  const { user, isUserLoading } = useCurrentUser();
  const { isCoach: isCoachPath, isOverview } = useView();
  const location = useLocation();
  const { isFeatureLocked } = useFeatureLocked(user);

  useEffect(() => {
    document.title = 'Mento | Coaching sessions';
  }, []);

  const isInMembers = location.pathname.includes('/coach/members');
  const isMemberPresentation = !isOverview && !isCoachPath;

  return (
    <div className="flex-1 overflow-x-hidden">
      <div
        // Page titles sit 48px from the top on every page: the member title is
        // laid out directly, the coach title adds PageHeaderLayout's own 32px.
        className={clsx('mx-auto flex h-full flex-1 flex-col px-4', {
          'container max-w-screen-lg': !isMemberPresentation,
          'py-4': !isMemberPresentation && !isInMembers,
          'py-8': isInMembers,
          'w-full max-w-[960px] py-12 md:px-0': isMemberPresentation,
        })}
      >
        {!isInMembers &&
          (isMemberPresentation ? (
            <Typography variant="h4">Coaching sessions</Typography>
          ) : (
            <PageHeaderLayout title="Coaching Sessions">
              {!isUserLoading &&
                !isOverview &&
                isCoachPath &&
                isCoach(user) && <CoachCalInfo coach={user} />}
            </PageHeaderLayout>
          ))}
        {isFeatureLocked && <LockedFeature title="sessions" />}
        {!isFeatureLocked && children}
      </div>
    </div>
  );
};

export default SessionsLayout;
